/**
 * KPI cards row on the dashboard: Revenue · Active Orders · Inventory ·
 * Production Efficiency · Receivables · Payables · Clients.
 *
 * Permission-aware — each card has an `id` mapped to a module permission.
 * Financial cards (revenue, inventory value, receivables, payables) are also
 * gated behind the `canViewFinancials` flag (admin/owner/manager only).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, ShoppingCart, Boxes, Factory, IndianRupee,
  CreditCard, Users, ArrowRight,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n || 0);

const KPI_PERM = {
  revenue:     'sales',
  orders:      'sales',
  inventory:   'inventory',
  production:  'production',
  receivables: 'debtors',
  payables:    'creditors',
  clients:     'debtors',
};
const FINANCIAL_KPIS = new Set(['revenue', 'inventory', 'receivables', 'payables']);

export default function KpiCardsWidget({ stats, user }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canViewFinancials = isAdmin || isManager;
  const perms = user?.permissions || {};
  const canSeeKpi = (id) => {
    if (FINANCIAL_KPIS.has(id) && !canViewFinancials) return false;
    return isAdmin || perms[KPI_PERM[id]] === true;
  };

  const invBreakdown = stats?.inventory_value_breakdown || {};
  const _has = (v) => v !== undefined && v !== null;

  const kpiCards = [
    { id: 'revenue',     label: 'Total Revenue',             value: fmt(stats?.total_revenue),               icon: IndianRupee,  color: 'text-amber-400',  bg: 'bg-amber-500/10',   change: stats?.revenue_change_pct ? `${stats.revenue_change_pct > 0 ? '+' : ''}${stats.revenue_change_pct}%` : '—', up: (stats?.revenue_change_pct || 0) >= 0 },
    { id: 'orders',      label: 'Active Orders',             value: stats?.active_orders || 0,               icon: ShoppingCart, color: 'text-blue-400',   bg: 'bg-blue-500/10',    change: _has(stats?.active_orders) ? `${stats.active_orders} pending` : '—', up: true  },
    { id: 'inventory',   label: 'Total Inventory Value',     value: fmt(stats?.inventory_value),             icon: Boxes,        color: 'text-green-400',  bg: 'bg-green-500/10',   change: _has(stats?.inventory_value) ? `RM ${fmt(invBreakdown.raw_materials)} · FG ${fmt(invBreakdown.finished_goods)}` : '—', up: true },
    { id: 'production',  label: 'Production Efficiency',     value: `${stats?.production_efficiency || 0}%`, icon: Factory,      color: 'text-purple-400', bg: 'bg-purple-500/10',  change: _has(stats?.total_batches) ? `${stats.completed_batches || 0}/${stats.total_batches} batches` : '—', up: true },
    { id: 'receivables', label: 'Outstanding Receivables',   value: fmt(stats?.outstanding_receivables),     icon: TrendingUp,   color: 'text-red-400',    bg: 'bg-red-500/10',     change: 'Unpaid/partial', up: false },
    { id: 'payables',    label: 'Outstanding Payables',      value: fmt(stats?.outstanding_payables),        icon: CreditCard,   color: 'text-orange-400', bg: 'bg-orange-500/10',  change: 'Purchase bills', up: false },
    { id: 'clients',     label: 'Total Clients',             value: stats?.total_clients || 0,               icon: Users,        color: 'text-cyan-400',   bg: 'bg-cyan-500/10',    change: 'Debtors & Creditors', up: true  },
  ].filter(c => canSeeKpi(c.id));

  if (kpiCards.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 order-[-2]" data-testid="kpi-cards">
      {kpiCards.map(({ id, label, value, icon: Icon, color, bg, change, up }) => (
        <button key={id} onClick={() => navigate('/analytics/' + id)} data-testid={`kpi-${id}`}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/40 hover:bg-zinc-800/50 transition-all group text-left cursor-pointer relative">
          <div className="flex items-center justify-between mb-3">
            <span className="text-zinc-400 text-xs font-medium">{label}</span>
            <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
              <Icon size={16} className={color} />
            </div>
          </div>
          <div className="font-heading font-semibold text-2xl text-zinc-50 font-mono">{value}</div>
          <div className={`text-xs mt-1.5 flex items-center gap-1 ${up ? 'text-green-400' : 'text-yellow-400'}`}>
            {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {change}
          </div>
          <ArrowRight size={11} className="text-zinc-700 group-hover:text-amber-400 absolute top-3 right-3 transition-colors" />
        </button>
      ))}
    </div>
  );
}
